import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import PublicNav from '../components/PublicNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Shield, Smartphone, Tablet, Laptop, Monitor, ChevronRight, Loader2, CheckCircle2,
  AlertTriangle, Zap, Battery, HardDrive, Wifi, Bug, Thermometer, Info, Sparkles,
  Mail, Terminal, Copy, X, ArrowRight, BarChart3, Send, Eye,
} from 'lucide-react';

const DEVICE_ICON = {
  iphone: Smartphone, android_phone: Smartphone,
  ipad: Tablet, android_tablet: Tablet,
  windows_pc: Laptop, mac: Laptop, linux: Monitor, chromebook: Laptop,
};

const SYMPTOM_ICON = {
  slow: Zap, battery: Battery, storage: HardDrive, overheat: Thermometer,
  crashing: AlertTriangle, no_boot: AlertTriangle, network: Wifi,
  virus: Bug, water: AlertTriangle, screen: AlertTriangle, audio: AlertTriangle, other: Info,
};

const SEVERITY_COLOR = {
  low: 'bg-gray-700 text-gray-200',
  medium: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  high: 'bg-orange-500/20 text-orange-300 border-orange-500/40',
  critical: 'bg-red-500/20 text-red-300 border-red-500/40',
};

const SCORE_COLOR = (s) => {
  if (s >= 85) return { ring: '#10B981', text: 'text-emerald-400', bg: 'from-emerald-500/20 to-[#0A0A0A]' };
  if (s >= 70) return { ring: '#D4A843', text: 'text-[#D4A843]', bg: 'from-[#D4A843]/20 to-[#0A0A0A]' };
  if (s >= 50) return { ring: '#F59E0B', text: 'text-amber-400', bg: 'from-amber-500/20 to-[#0A0A0A]' };
  return { ring: '#EF4444', text: 'text-red-400', bg: 'from-red-500/20 to-[#0A0A0A]' };
};

// ── Score ring (SVG) ───────────────────────────────────────────
function ScoreRing({ score = 0, label = '' }) {
  const color = SCORE_COLOR(score);
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-36">
        <svg className="w-36 h-36 -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="54" fill="none" stroke="#1a1a1a" strokeWidth="10" />
          <circle cx="60" cy="60" r="54" fill="none" stroke={color.ring} strokeWidth="10"
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 900ms ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-4xl font-bold ${color.text}`}>{score}</span>
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">of 100</span>
        </div>
      </div>
      <p className={`mt-2 text-sm font-semibold ${color.text}`}>{label}</p>
    </div>
  );
}

// ── Plans card ─────────────────────────────────────────────────
function PlanCard({ plan, recommended, onChoose, submitting }) {
  return (
    <div className={`relative bg-[#111] border rounded-2xl p-6 flex flex-col ${
      recommended ? 'border-[#D4A843] shadow-[0_0_40px_rgba(212,168,67,0.15)]' : 'border-gray-800'
    }`} data-testid={`plan-${plan.id}`}>
      {plan.popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold px-3 py-1 rounded-full bg-[#D4A843] text-[#0A0A0A] uppercase tracking-wide">Most Popular</span>
      )}
      {recommended && !plan.popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold px-3 py-1 rounded-full bg-[#7C3AED] text-white uppercase tracking-wide">Recommended</span>
      )}
      <h3 className="text-lg font-bold text-white">{plan.name}</h3>
      <p className="text-xs text-gray-400 mt-0.5">{plan.tagline}</p>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="text-4xl font-bold text-white">${plan.price.toFixed(0)}</span>
        <span className="text-sm text-gray-500">/{plan.interval}</span>
      </div>
      <ul className="mt-5 space-y-2 flex-1">
        {plan.features.map((f, i) => (
          <li key={`f-${i}`} className="flex items-start gap-2 text-xs text-gray-300">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#D4A843] flex-shrink-0 mt-0.5" />{f}
          </li>
        ))}
      </ul>
      <Button
        onClick={() => onChoose(plan)}
        disabled={submitting}
        className={`mt-6 w-full rounded-lg font-semibold ${
          recommended ? 'bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A]' : 'bg-white/5 hover:bg-white/10 text-white border border-gray-700'
        }`}
        data-testid={`plan-${plan.id}-cta`}
      >
        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Subscribe'}
      </Button>
    </div>
  );
}

export default function DeviceDiagnostics() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'platform_admin' || user.role === 'business_admin');

  // Step flow: 'intake' → 'analyzing' → 'results' → 'plans'
  const [step, setStep] = useState('intake');
  const [meta, setMeta] = useState({ devices: [], symptoms: [] });
  const [plans, setPlans] = useState([]);

  // Intake state
  const [deviceId, setDeviceId] = useState('');
  const [symptoms, setSymptoms] = useState([]);
  const [description, setDescription] = useState('');
  const [telemetry, setTelemetry] = useState('');
  const [showTelemetry, setShowTelemetry] = useState(false);

  // Result state
  const [reportId, setReportId] = useState('');
  const [device, setDevice] = useState(null);
  const [report, setReport] = useState(null);

  // Lead capture
  const [leadEmail, setLeadEmail] = useState('');
  const [leadName, setLeadName] = useState('');
  const [leadSent, setLeadSent] = useState(false);
  const [sendingLead, setSendingLead] = useState(false);

  // Checkout state
  const [subscribing, setSubscribing] = useState(false);

  // Script modal
  const [scriptOs, setScriptOs] = useState(null);
  const [scriptBody, setScriptBody] = useState('');

  // Admin master weekly digest (diagnostics + marketplace + applications + subscriptions + tickets)
  const [digest, setDigest] = useState(null);
  const [digestSending, setDigestSending] = useState(false);
  const [digestPreviewOpen, setDigestPreviewOpen] = useState(false);

  const loadDigest = async () => {
    try {
      const { data } = await api.get('/api/admin/digest/preview?days=7');
      setDigest(data);
      return data;
    } catch (e) {
      toast.error('Could not load master digest');
    }
  };

  const sendDigestNow = async () => {
    setDigestSending(true);
    try {
      const { data } = await api.post('/api/admin/digest/send', { days: 7 });
      toast.success(`Master digest sent to ${data.sent.length} admin${data.sent.length === 1 ? '' : 's'}`);
      if (data.failed?.length) toast.warning(`${data.failed.length} delivery failed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    }
    setDigestSending(false);
  };

  useEffect(() => {
    if (isAdmin) loadDigest();
  }, [isAdmin]);

  useEffect(() => {
    (async () => {
      try {
        const [m, p] = await Promise.all([
          api.get('/api/diagnostics/metadata'),
          api.get('/api/diagnostics/plans'),
        ]);
        setMeta(m.data);
        setPlans(p.data.plans || []);
      } catch (e) {
        toast.error('Could not load diagnostic options');
      }
    })();
  }, []);

  const toggleSymptom = (id) => {
    setSymptoms((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };

  const runDiagnostic = async () => {
    if (!deviceId) { toast.error('Pick a device type'); return; }
    setStep('analyzing');
    try {
      const payload = { device_id: deviceId, symptoms, description };
      if (telemetry.trim()) {
        try { payload.telemetry = JSON.parse(telemetry); }
        catch { payload.telemetry = telemetry; }
      }
      const { data } = await api.post('/api/diagnostics/run', payload);
      setReportId(data.id);
      setDevice(data.device);
      setReport(data.report);
      setStep('results');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed');
      setStep('intake');
    }
  };

  const sendReport = async () => {
    if (!leadEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    setSendingLead(true);
    try {
      await api.post('/api/diagnostics/email-report', { report_id: reportId, email: leadEmail, name: leadName });
      setLeadSent(true);
      toast.success('Report sent to your inbox');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    }
    setSendingLead(false);
  };

  const chooseToFix = () => {
    // Carry the report ID over so the marketplace pre-fills
    navigate(`/marketplace?from=diagnostics&report_id=${encodeURIComponent(reportId)}`);
  };

  const chooseToLearn = () => navigate('/train');

  const subscribe = async (plan) => {
    if (!leadEmail.includes('@')) { toast.error('Enter your email above first to subscribe'); return; }
    setSubscribing(true);
    try {
      const { data } = await api.post('/api/diagnostics/subscribe', {
        plan_id: plan.id,
        email: leadEmail,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    }
    setSubscribing(false);
  };

  const loadScript = async (osType) => {
    try {
      const { data } = await api.get(`/api/diagnostics/scripts/${osType}`);
      setScriptBody(typeof data === 'string' ? data : JSON.stringify(data, null, 2));
      setScriptOs(osType);
    } catch (e) {
      toast.error('Script unavailable');
    }
  };

  const copyScript = () => {
    navigator.clipboard.writeText(scriptBody);
    toast.success('Script copied — run it on your device, paste the output back in the form');
  };

  const recommendedPlanId = report?.subscription_fit
    ? (report.subscription_fit === 'business' ? 'device_care_business'
      : report.subscription_fit === 'family' ? 'device_care_family'
      : 'device_care_personal')
    : null;

  // ── Analyzing screen ─────────────────────────────────────────
  if (step === 'analyzing') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6" data-testid="diag-analyzing">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 mx-auto mb-6 relative">
            <div className="absolute inset-0 rounded-full bg-[#D4A843]/10 animate-ping" />
            <div className="relative w-20 h-20 rounded-full bg-[#D4A843]/20 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-[#D4A843] animate-pulse" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Diagnosing your device…</h2>
          <p className="text-sm text-gray-400">Our AI technician is checking symptoms, matching patterns, and estimating repair scope. This usually takes 5-10 seconds.</p>
        </div>
      </div>
    );
  }

  // ── Results screen ───────────────────────────────────────────
  if (step === 'results' && report && device) {
    const color = SCORE_COLOR(report.health_score);
    const prof = report.professional_fix || {};
    return (
      <div className={`min-h-screen bg-gradient-to-b ${color.bg} pb-24`} data-testid="diag-results">
        <PublicNav />
        <div className="max-w-5xl mx-auto px-6 pt-4 pb-2 flex justify-end">
          <button onClick={() => { setStep('intake'); setReport(null); }} className="text-xs text-gray-400 hover:text-white" data-testid="diag-restart">← Start over</button>
        </div>

        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-8 mb-10 bg-[#111]/60 backdrop-blur border border-gray-800 rounded-2xl p-8">
            <ScoreRing score={report.health_score} label={report.severity_label} />
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{device.name} · {device.os}</p>
              <h1 className="text-2xl md:text-3xl font-bold text-white mt-1">Your Device Health Report</h1>
              <p className="text-gray-300 mt-3 leading-relaxed">{report.summary}</p>
            </div>
          </div>

          {/* Issues */}
          {report.issues?.length > 0 && (
            <section className="mb-10">
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#D4A843] mb-3">Detected Issues</h2>
              <div className="grid gap-3">
                {report.issues.map((issue, i) => (
                  <div key={`iss-${i}`} className="bg-[#111] border border-gray-800 rounded-xl p-4 flex items-start gap-3" data-testid={`diag-issue-${i}`}>
                    <AlertTriangle className="w-4 h-4 text-[#D4A843] flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-white">{issue.title}</h3>
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${SEVERITY_COLOR[issue.severity] || SEVERITY_COLOR.low}`}>{issue.severity}</span>
                        <span className="text-[10px] text-gray-500 uppercase">{issue.category}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{issue.explanation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* DIY fixes */}
          {report.diy_fixes?.length > 0 && (
            <section className="mb-10">
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#D4A843] mb-3">Try These Yourself (Free)</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {report.diy_fixes.map((fix, i) => (
                  <div key={`fix-${i}`} className="bg-[#111] border border-gray-800 rounded-xl p-4" data-testid={`diag-fix-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-sm font-semibold text-white">{fix.title}</h3>
                      <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-gray-700 text-gray-200">{fix.difficulty}</span>
                    </div>
                    <ol className="text-xs text-gray-400 space-y-1 list-decimal list-inside">
                      {fix.steps?.slice(0, 6).map((s, j) => <li key={`s-${j}`}>{s}</li>)}
                    </ol>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 3 CTAs */}
          <section className="mb-10">
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#D4A843] mb-3">What's next?</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <button onClick={chooseToFix} className="group bg-[#111] border border-gray-800 hover:border-[#D4A843] rounded-2xl p-6 text-left transition-all" data-testid="diag-cta-fix">
                <div className="w-10 h-10 rounded-lg bg-[#D4A843]/15 flex items-center justify-center mb-3"><Shield className="w-5 h-5 text-[#D4A843]" /></div>
                <h3 className="text-base font-bold text-white mb-1">Fix it for me</h3>
                <p className="text-xs text-gray-400 mb-3">Route this report to a certified technician.</p>
                {prof.estimated_cost_range && <p className="text-xs text-[#D4A843] font-semibold">Est. {prof.estimated_cost_range}</p>}
                <span className="text-xs text-[#D4A843] inline-flex items-center gap-1 mt-3 group-hover:gap-2 transition-all">Open marketplace <ChevronRight className="w-3 h-3" /></span>
              </button>

              <button onClick={chooseToLearn} className="group bg-[#111] border border-gray-800 hover:border-[#7C3AED] rounded-2xl p-6 text-left transition-all" data-testid="diag-cta-learn">
                <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/15 flex items-center justify-center mb-3"><Sparkles className="w-5 h-5 text-[#7C3AED]" /></div>
                <h3 className="text-base font-bold text-white mb-1">Learn to fix it yourself</h3>
                <p className="text-xs text-gray-400 mb-3">Hands-on labs at TechOps Academy.</p>
                <p className="text-xs text-gray-500 font-semibold">Free & paid tracks</p>
                <span className="text-xs text-[#7C3AED] inline-flex items-center gap-1 mt-3 group-hover:gap-2 transition-all">Open training <ChevronRight className="w-3 h-3" /></span>
              </button>

              <button onClick={() => setStep('plans')} className="group bg-[#111] border border-gray-800 hover:border-emerald-500 rounded-2xl p-6 text-left transition-all" data-testid="diag-cta-subscribe">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center mb-3"><CheckCircle2 className="w-5 h-5 text-emerald-400" /></div>
                <h3 className="text-base font-bold text-white mb-1">Monthly monitoring</h3>
                <p className="text-xs text-gray-400 mb-3">Auto re-scan + alerts + priority help.</p>
                <p className="text-xs text-emerald-400 font-semibold">From $9/mo</p>
                <span className="text-xs text-emerald-400 inline-flex items-center gap-1 mt-3 group-hover:gap-2 transition-all">See plans <ChevronRight className="w-3 h-3" /></span>
              </button>
            </div>
          </section>

          {/* Lead capture */}
          <section className="mb-10 bg-gradient-to-br from-[#111] to-[#1a1a1a] border border-[#D4A843]/30 rounded-2xl p-6" data-testid="diag-lead">
            <div className="flex items-start gap-4">
              <Mail className="w-6 h-6 text-[#D4A843] flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-base font-bold text-white mb-1">Email me this report</h3>
                <p className="text-xs text-gray-400 mb-4">We'll send a PDF-friendly copy with the issues, DIY steps, and service options so you can reference it later.</p>
                {leadSent ? (
                  <div className="flex items-center gap-2 text-sm text-emerald-400"><CheckCircle2 className="w-4 h-4" /> Sent to {leadEmail}</div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="Your name (optional)" className="bg-[#0A0A0A] border-gray-700 text-white rounded-lg" data-testid="diag-lead-name" />
                    <Input value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} placeholder="you@example.com" type="email" className="bg-[#0A0A0A] border-gray-700 text-white rounded-lg" data-testid="diag-lead-email" />
                    <Button onClick={sendReport} disabled={sendingLead} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg whitespace-nowrap" data-testid="diag-lead-send">
                      {sendingLead ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send report'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Deep scan scripts */}
          <section className="mb-10">
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#D4A843] mb-3 flex items-center gap-1"><Terminal className="w-3 h-3" /> Advanced: Deep Scan</h2>
            <p className="text-xs text-gray-400 mb-3">Run a script on your computer and paste the telemetry output for a sharper analysis. Mobile devices: skip this.</p>
            <div className="flex flex-wrap gap-2">
              {['windows', 'macos', 'linux'].map((os) => (
                <button key={os} onClick={() => loadScript(os)} className="px-4 py-2 bg-[#111] border border-gray-800 hover:border-[#D4A843] text-xs text-white rounded-lg capitalize" data-testid={`diag-script-${os}`}>
                  <Terminal className="w-3 h-3 inline mr-1.5" />{os} script
                </button>
              ))}
            </div>
          </section>
        </div>

        {/* Script modal */}
        {scriptOs && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setScriptOs(null)}>
            <div className="bg-[#111] border border-gray-800 rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-800">
                <h3 className="text-sm font-bold text-white capitalize">{scriptOs} Deep Scan Script</h3>
                <div className="flex gap-2">
                  <Button size="sm" onClick={copyScript} className="bg-[#D4A843] text-[#0A0A0A] text-xs rounded-lg h-8" data-testid="diag-script-copy"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                  <button onClick={() => setScriptOs(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <pre className="flex-1 overflow-auto p-4 text-[11px] text-emerald-300 font-mono whitespace-pre">{scriptBody}</pre>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Plans screen ─────────────────────────────────────────────
  if (step === 'plans') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pb-24" data-testid="diag-plans">
        <PublicNav />
        <div className="max-w-5xl mx-auto px-6 pt-4 pb-2 flex justify-end">
          <button onClick={() => setStep('results')} className="text-xs text-gray-400 hover:text-white" data-testid="diag-back-results">← Back to results</button>
        </div>

        <div className="max-w-5xl mx-auto px-6 pt-8">
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Device Care Plans</h1>
            <p className="text-gray-400 max-w-2xl mx-auto">Automated monthly scans, security alerts, and priority support. Cancel anytime. 7-day refund if it's not a fit.</p>
          </div>

          {/* Email collector for subscription */}
          {!leadEmail && (
            <div className="max-w-md mx-auto mb-8 bg-[#111] border border-gray-800 rounded-xl p-4">
              <Label className="text-xs text-gray-400">Email for your subscription</Label>
              <Input value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} placeholder="you@example.com" type="email" className="mt-1 bg-[#0A0A0A] border-gray-700 text-white rounded-lg" data-testid="diag-plans-email" />
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-5">
            {plans.map((p) => (
              <PlanCard key={p.id} plan={p} recommended={recommendedPlanId === p.id} onChoose={subscribe} submitting={subscribing} />
            ))}
          </div>

          <p className="text-center text-[10px] text-gray-500 mt-10">Secure checkout by Stripe · Cancel from your account at any time</p>
        </div>
      </div>
    );
  }

  // ── Intake screen (default) ──────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="diag-intake">
      <PublicNav />

      {/* Admin-only master digest banner — diagnostics + marketplace + applications + subscriptions + tickets */}
      {isAdmin && digest && (
        <div className="max-w-5xl mx-auto px-6" data-testid="diag-admin-digest">
          <div className="bg-gradient-to-r from-[#D4A843]/10 to-[#7C3AED]/10 border border-[#D4A843]/30 rounded-2xl p-5 mb-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4A843] flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-[#0A0A0A]" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#D4A843]">Your Week at TechOps · last 7 days</p>
                  <p className="text-xs text-gray-400 mt-0.5">Auto-sends every Monday at 09:00 UTC · click Send Now for an ad-hoc copy</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setDigestPreviewOpen(true)} className="border-gray-700 text-white hover:bg-white/5 rounded-lg text-xs h-9" data-testid="digest-preview-btn"><Eye className="w-3.5 h-3.5 mr-1.5" /> Preview</Button>
                <Button size="sm" onClick={sendDigestNow} disabled={digestSending} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs h-9" data-testid="digest-send-btn">
                  {digestSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Send now</>}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
              {[
                { label: 'MRR', value: `$${(digest.stats.subscriptions?.mrr || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, sub: `${digest.stats.subscriptions?.total_subscribers || 0} subs`, color: 'text-emerald-400' },
                { label: 'Diagnostic Leads', value: digest.stats.diagnostics?.leads_captured || 0, sub: `${digest.stats.diagnostics?.total_scans || 0} scans`, color: 'text-[#D4A843]' },
                { label: 'New Requests', value: digest.stats.marketplace?.new_requests || 0, sub: `${digest.stats.marketplace?.completed || 0} done`, color: 'text-white' },
                { label: 'New Applicants', value: (digest.stats.applications?.freelancer_new || 0) + (digest.stats.applications?.partner_new || 0), sub: `${digest.stats.applications?.freelancer_pending_total || 0} pending`, color: 'text-[#7C3AED]' },
                { label: 'Tickets', value: digest.stats.tickets?.new || 0, sub: `${digest.stats.tickets?.sla_breaches || 0} SLA`, color: digest.stats.tickets?.sla_breaches ? 'text-red-400' : 'text-white' },
              ].map((stat) => (
                <div key={stat.label} className="bg-[#0A0A0A]/60 border border-gray-800 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">{stat.label}</p>
                  <p className={`mt-1 text-xl font-bold ${stat.color}`}>{stat.value}</p>
                  {stat.sub && <p className="text-[10px] text-gray-500 mt-0.5">{stat.sub}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Admin digest preview modal */}
      {digestPreviewOpen && digest && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setDigestPreviewOpen(false)} data-testid="digest-preview-modal">
          <div className="bg-[#111] border border-gray-800 rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h3 className="text-sm font-bold text-white">Master Weekly Digest Preview</h3>
              <button onClick={() => setDigestPreviewOpen(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <iframe
              title="Digest preview"
              srcDoc={digest.html}
              className="flex-1 w-full bg-[#0A0A0A]"
              sandbox=""
            />
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <span className="inline-block text-[10px] font-bold px-3 py-1 rounded-full bg-[#D4A843]/15 text-[#D4A843] uppercase tracking-wider mb-4">Free · 60 seconds</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">Run a free health check on your device</h1>
          <p className="text-lg text-gray-400 max-w-xl mx-auto">AI-powered diagnostics for phones, tablets, laptops and desktops. Get a score, fix steps, and a repair estimate — instantly.</p>
        </div>

        {/* Device picker */}
        <section className="mb-8">
          <h2 className="text-xs font-bold uppercase tracking-wider text-[#D4A843] mb-3">1 · What device are we checking?</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {meta.devices.map((d) => {
              const Icon = DEVICE_ICON[d.id] || Laptop;
              const active = deviceId === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => setDeviceId(d.id)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${
                    active ? 'bg-[#D4A843]/10 border-[#D4A843] shadow-[0_0_20px_rgba(212,168,67,0.2)]' : 'bg-[#111] border-gray-800 hover:border-gray-600'
                  }`}
                  data-testid={`diag-device-${d.id}`}
                >
                  <Icon className={`w-6 h-6 ${active ? 'text-[#D4A843]' : 'text-gray-500'}`} />
                  <span className={`text-xs font-medium ${active ? 'text-[#D4A843]' : 'text-gray-300'}`}>{d.name}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Symptoms */}
        <section className="mb-8">
          <h2 className="text-xs font-bold uppercase tracking-wider text-[#D4A843] mb-3">2 · What's wrong? <span className="text-gray-500 normal-case">(check all that apply)</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {meta.symptoms.map((s) => {
              const Icon = SYMPTOM_ICON[s.id] || Info;
              const active = symptoms.includes(s.id);
              return (
                <button
                  key={s.id}
                  onClick={() => toggleSymptom(s.id)}
                  className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                    active ? 'bg-[#D4A843]/10 border-[#D4A843]' : 'bg-[#111] border-gray-800 hover:border-gray-600'
                  }`}
                  data-testid={`diag-symptom-${s.id}`}
                >
                  <Icon className={`w-4 h-4 ${active ? 'text-[#D4A843]' : 'text-gray-500'}`} />
                  <span className={`text-sm ${active ? 'text-white' : 'text-gray-300'}`}>{s.label}</span>
                  {active && <CheckCircle2 className="w-4 h-4 text-[#D4A843] ml-auto" />}
                </button>
              );
            })}
          </div>
        </section>

        {/* Description */}
        <section className="mb-8">
          <h2 className="text-xs font-bold uppercase tracking-wider text-[#D4A843] mb-3">3 · Describe it in your words <span className="text-gray-500 normal-case">(optional)</span></h2>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., My 4-year-old laptop takes 3 minutes to boot, fan runs constantly, and I get low disk warnings weekly."
            className="w-full bg-[#111] border border-gray-800 rounded-lg p-4 text-sm text-white placeholder:text-gray-600 min-h-[100px]"
            data-testid="diag-description"
          />
        </section>

        {/* Advanced telemetry */}
        <section className="mb-8">
          <button onClick={() => setShowTelemetry((v) => !v)} className="text-xs text-gray-400 hover:text-[#D4A843] flex items-center gap-1" data-testid="diag-telemetry-toggle">
            <Terminal className="w-3 h-3" /> Have diagnostic output from a script? Paste it here {showTelemetry ? '▴' : '▾'}
          </button>
          {showTelemetry && (
            <textarea
              value={telemetry}
              onChange={(e) => setTelemetry(e.target.value)}
              placeholder="Paste JSON telemetry from our Windows / macOS / Linux deep-scan script…"
              className="mt-3 w-full bg-[#111] border border-gray-800 rounded-lg p-3 text-[11px] font-mono text-emerald-300 placeholder:text-gray-600 min-h-[80px]"
              data-testid="diag-telemetry"
            />
          )}
        </section>

        <Button
          onClick={runDiagnostic}
          disabled={!deviceId}
          className="w-full bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-bold text-base rounded-lg py-6 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="diag-run"
        >
          Run Free Health Check <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <p className="text-center text-[10px] text-gray-500 mt-3">No signup required · Results in under 10 seconds · We never share your data</p>
      </div>
    </div>
  );
}
